import React, { useMemo, useState } from 'react';

import {
  Activity,
  BarChart3,
  Image as ImageIcon,
  Layers,
  ReceiptText,
  RefreshCw,
  Table2,
  Users,
  Video,
} from 'lucide-react';

import Modal from '@/components/common/Modal';
import type {
  BillingModelUsage,
  GlobalBillingDetailUsage,
  GlobalBillingUsage,
  GlobalBillingUserUsage,
} from '@/types';

import {
  type BillingDateRange,
  formatBillingCategory,
  formatBillingPoints,
  formatPointCount,
  getBillingModelPoints,
  getGlobalBillingSummaryItems,
  getMediaBillingPoints,
} from '../utils/billingUsageFormat';
import BillingDateRangeFilter from './BillingDateRangeFilter';

interface GlobalBillingUsageModalProps {
  open: boolean;
  usage: GlobalBillingUsage | null;
  loading: boolean;
  error: string | null;
  dateRange: BillingDateRange;
  onClose: () => void;
  onRetry: () => void;
  onDateRangeChange: (value: BillingDateRange) => void;
}

type ReportTab = 'summary' | 'daily' | 'users' | 'models' | 'details';

interface TabItem {
  key: ReportTab;
  label: string;
  icon: typeof BarChart3;
}

interface TrendPoint {
  label: string;
  value: number;
}

interface ModelPieSegment {
  label: string;
  value: number;
  percent: number;
  color: string;
  dash: number;
  offset: number;
}

const tabs: TabItem[] = [
  { key: 'summary', label: '总汇总', icon: ReceiptText },
  { key: 'daily', label: '天维度汇总', icon: BarChart3 },
  { key: 'users', label: '用户维度汇总', icon: Users },
  { key: 'models', label: '模型维度汇总', icon: Layers },
  { key: 'details', label: '积分明细', icon: Table2 },
];

const summaryIconByLabel: Record<string, typeof Activity> = {
  总积分: ReceiptText,
  用户数: Users,
  模型数: Layers,
  生图次数: ImageIcon,
  生视频次数: Video,
};

const formatDateLabel = (date: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date || '-';
  return `${Number(match[2])}月${Number(match[3])}日`;
};

const formatNullableText = (value: string | null | undefined): string => {
  return value?.trim() || '-';
};

const tableHeadClass = 'bg-dark-bg px-4 py-2.5 text-xs font-medium text-dark-text-secondary';
const tableCellClass = 'border-t border-dark-border px-4 py-3 text-sm text-dark-text';
const modelPieColors = [
  '#22d3ee',
  '#34d399',
  '#3b82f6',
  '#a78bfa',
  '#f59e0b',
  '#f472b6',
  '#94a3b8',
];

const EmptyBlock: React.FC<{ text: string }> = ({ text }) => (
  <div className="rounded-lg border border-dark-border bg-dark-card px-4 py-8 text-center text-sm text-dark-text-secondary">
    {text}
  </div>
);

const BillingTrendChart: React.FC<{
  title: string;
  data: TrendPoint[];
  color: string;
  valueFormatter?: (value: number) => string;
}> = ({ title, data, color, valueFormatter = (value) => String(value) }) => {
  const chart = useMemo(() => {
    const width = 420;
    const height = 220;
    const left = 48;
    const right = 18;
    const top = 26;
    const bottom = 42;
    const values = data.map((item) => item.value);
    const maxValue = Math.max(...values, 1);
    const plotWidth = width - left - right;
    const plotHeight = height - top - bottom;
    const points = data.map((item, index) => {
      const x =
        data.length === 1 ? left + plotWidth / 2 : left + (plotWidth * index) / (data.length - 1);
      const y = top + plotHeight - (item.value / maxValue) * plotHeight;
      return { ...item, x, y };
    });
    const path = points
      .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
      .join(' ');
    return { width, height, left, right, top, bottom, maxValue, plotHeight, points, path };
  }, [data]);

  if (data.length === 0) {
    return <EmptyBlock text={`${title}暂无数据`} />;
  }

  const tickValues = [chart.maxValue, chart.maxValue / 2, 0];
  const xLabels =
    data.length <= 2
      ? data
      : [data[0], data[Math.floor((data.length - 1) / 2)], data[data.length - 1]];

  return (
    <div className="rounded-lg border border-dark-border bg-dark-card p-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium text-dark-text">{title}</h4>
        <span className="text-xs text-dark-text-secondary">{data.length} 天</span>
      </div>
      <svg
        className="h-52 w-full overflow-visible"
        viewBox={`0 0 ${chart.width} ${chart.height}`}
        role="img"
        aria-label={title}
      >
        {tickValues.map((value) => {
          const y = chart.top + chart.plotHeight - (value / chart.maxValue) * chart.plotHeight;
          return (
            <g key={value}>
              <line
                x1={chart.left}
                x2={chart.width - chart.right}
                y1={y}
                y2={y}
                stroke="currentColor"
                className="text-dark-border"
                strokeDasharray="4 5"
              />
              <text
                x={chart.left - 10}
                y={y + 4}
                textAnchor="end"
                className="fill-dark-text-secondary text-[10px]"
              >
                {valueFormatter(value)}
              </text>
            </g>
          );
        })}
        <path d={chart.path} fill="none" stroke={color} strokeWidth="3" strokeLinejoin="round" />
        {chart.points.map((point) => (
          <g key={`${point.label}-${point.value}`}>
            <circle cx={point.x} cy={point.y} r="4" fill={color} />
            <circle cx={point.x} cy={point.y} r="8" fill={color} opacity="0.12" />
          </g>
        ))}
        {xLabels.map((item) => {
          const point = chart.points.find((candidate) => candidate.label === item.label);
          if (!point) return null;
          return (
            <text
              key={item.label}
              x={point.x}
              y={chart.height - 10}
              textAnchor="middle"
              className="fill-dark-text-secondary text-[10px]"
            >
              {formatDateLabel(item.label)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const ModelPointsPieChart: React.FC<{ models: BillingModelUsage[] }> = ({ models }) => {
  const chart = useMemo(() => {
    const radius = 58;
    const circumference = 2 * Math.PI * radius;
    const grouped = new Map<string, number>();

    models.forEach((item) => {
      grouped.set(item.model, (grouped.get(item.model) ?? 0) + getBillingModelPoints(item));
    });

    const sorted = Array.from(grouped.entries())
      .map(([label, value]) => ({ label, value }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);

    const visible = sorted.slice(0, 6);
    const rest = sorted.slice(6);
    const normalized =
      rest.length > 0
        ? [
            ...visible,
            { label: '其他模型', value: rest.reduce((sum, item) => sum + item.value, 0) },
          ]
        : visible;
    const total = normalized.reduce((sum, item) => sum + item.value, 0);

    let offset = 0;
    const segments: ModelPieSegment[] = normalized.map((item, index) => {
      const dash = total > 0 ? (item.value / total) * circumference : 0;
      const segment = {
        label: item.label,
        value: item.value,
        percent: total > 0 ? (item.value / total) * 100 : 0,
        color: modelPieColors[index % modelPieColors.length],
        dash,
        offset,
      };
      offset += dash;
      return segment;
    });

    return { circumference, radius, segments, total };
  }, [models]);

  if (chart.segments.length === 0) {
    return <EmptyBlock text="暂无模型积分占比" />;
  }

  return (
    <div className="rounded-lg border border-dark-border bg-dark-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <h4 className="text-sm font-medium text-dark-text">模型积分占比</h4>
        <span className="text-xs text-dark-text-secondary">{chart.segments.length} 项</span>
      </div>
      <div className="flex flex-col items-center">
        <svg className="h-48 w-48" viewBox="0 0 160 160" role="img" aria-label="模型积分占比">
          <circle cx="80" cy="80" r={chart.radius} fill="none" stroke="#2a2d33" strokeWidth="28" />
          {chart.segments.map((segment) => (
            <circle
              key={segment.label}
              cx="80"
              cy="80"
              r={chart.radius}
              fill="none"
              stroke={segment.color}
              strokeDasharray={`${segment.dash} ${chart.circumference - segment.dash}`}
              strokeDashoffset={-segment.offset}
              strokeWidth="28"
              transform="rotate(-90 80 80)"
            />
          ))}
          <text x="80" y="76" textAnchor="middle" className="fill-dark-text-secondary text-[10px]">
            总积分
          </text>
          <text
            x="80"
            y="94"
            textAnchor="middle"
            className="fill-dark-text text-[16px] font-semibold"
          >
            {formatPointCount(chart.total)}
          </text>
        </svg>
      </div>
      <div className="mt-3 max-h-40 space-y-2 overflow-auto pr-1">
        {chart.segments.map((segment) => (
          <div
            key={segment.label}
            className="grid grid-cols-[minmax(0,1fr)_64px_44px] gap-2 text-xs"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: segment.color }}
              />
              <span className="truncate text-dark-text" title={segment.label}>
                {segment.label}
              </span>
            </div>
            <span className="text-right tabular-nums text-dark-text">
              {formatPointCount(segment.value)}
            </span>
            <span className="text-right tabular-nums text-dark-text-secondary">
              {segment.percent.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SummaryTab: React.FC<{ usage: GlobalBillingUsage }> = ({ usage }) => {
  const userTrend = usage.daily.map((item) => ({
    label: item.date,
    value: item.active_user_count,
  }));
  const pointsTrend = usage.daily.map((item) => ({
    label: item.date,
    value: getMediaBillingPoints(item),
  }));
  const rows = [
    { label: '用户数', count: usage.active_user_count, points: null },
    { label: '模型数', count: usage.models.length, points: null },
    {
      label: '生图次数',
      count: usage.image_task_count,
      points: formatBillingPoints(usage.image_cost, usage.image_task_count),
    },
    {
      label: '生视频次数',
      count: usage.video_task_count,
      points: formatBillingPoints(usage.video_cost, usage.video_task_count),
    },
    {
      label: '总任务数',
      count: usage.billed_task_count,
      points: formatPointCount(getMediaBillingPoints(usage)),
    },
  ];

  return (
    <div className="space-y-4">
      <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <BillingTrendChart title="用户数趋势" data={userTrend} color="rgb(var(--color-theme))" />
        <BillingTrendChart
          title="日积分趋势"
          data={pointsTrend}
          color="#3b82f6"
          valueFormatter={formatPointCount}
        />
      </section>

      <div className="rounded-lg border border-dark-border">
        <div className="grid grid-cols-[minmax(0,1fr)_120px_140px]">
          <div className={tableHeadClass}>指标</div>
          <div className={`${tableHeadClass} text-right`}>数量</div>
          <div className={`${tableHeadClass} text-right`}>积分</div>
          {rows.map((row) => (
            <React.Fragment key={row.label}>
              <div className={`${tableCellClass} font-medium`}>{row.label}</div>
              <div className={`${tableCellClass} text-right tabular-nums`}>{row.count}</div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {row.points === null ? '-' : row.points}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const DailyReportTab: React.FC<{ usage: GlobalBillingUsage }> = ({ usage }) => {
  return (
    <div className="space-y-4">
      <section>
        {usage.daily.length > 0 ? (
          <div className="max-h-[460px] overflow-auto rounded-lg border border-dark-border bg-dark-card">
            <div className="grid min-w-[900px] w-full grid-cols-[minmax(108px,1.15fr)_minmax(84px,0.85fr)_minmax(96px,1fr)_minmax(112px,1fr)_minmax(112px,1fr)_minmax(124px,1fr)_minmax(112px,1fr)]">
              <div className={tableHeadClass}>日期</div>
              <div className={`${tableHeadClass} text-right`}>用户数</div>
              <div className={`${tableHeadClass} text-right`}>生图次数</div>
              <div className={`${tableHeadClass} text-right`}>生图积分</div>
              <div className={`${tableHeadClass} text-right`}>生视频次数</div>
              <div className={`${tableHeadClass} text-right`}>生视频积分</div>
              <div className={`${tableHeadClass} text-right`}>总积分</div>
              {usage.daily.map((item) => (
                <React.Fragment key={item.date}>
                  <div className={`${tableCellClass} font-medium`}>
                    {formatDateLabel(item.date)}
                  </div>
                  <div className={`${tableCellClass} text-right tabular-nums`}>
                    {item.active_user_count}
                  </div>
                  <div className={`${tableCellClass} text-right tabular-nums`}>
                    {item.image_task_count}
                  </div>
                  <div className={`${tableCellClass} text-right tabular-nums`}>
                    {formatBillingPoints(item.image_cost, item.image_task_count)}
                  </div>
                  <div className={`${tableCellClass} text-right tabular-nums`}>
                    {item.video_task_count}
                  </div>
                  <div className={`${tableCellClass} text-right tabular-nums`}>
                    {formatBillingPoints(item.video_cost, item.video_task_count)}
                  </div>
                  <div className={`${tableCellClass} text-right font-medium tabular-nums`}>
                    {formatPointCount(getMediaBillingPoints(item))}
                  </div>
                </React.Fragment>
              ))}
              <div className={`${tableCellClass} bg-theme-color-10 font-semibold`}>合计</div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {usage.active_user_count}
              </div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {usage.image_task_count}
              </div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {formatBillingPoints(usage.image_cost, usage.image_task_count)}
              </div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {usage.video_task_count}
              </div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {formatBillingPoints(usage.video_cost, usage.video_task_count)}
              </div>
              <div
                className={`${tableCellClass} bg-theme-color-10 text-right font-semibold tabular-nums`}
              >
                {formatPointCount(getMediaBillingPoints(usage))}
              </div>
            </div>
          </div>
        ) : (
          <EmptyBlock text="暂无天维度数据" />
        )}
      </section>
    </div>
  );
};

const UsersReportTab: React.FC<{ users: GlobalBillingUserUsage[] }> = ({ users }) => {
  if (users.length === 0) {
    return <EmptyBlock text="暂无用户维度数据" />;
  }

  return (
    <div className="max-h-[460px] overflow-auto rounded-lg border border-dark-border">
      <div className="min-w-[860px]">
        <div className="grid grid-cols-[minmax(132px,1.1fr)_minmax(132px,1fr)_82px_92px_112px_104px_124px_112px]">
          <div className={tableHeadClass}>用户 ID</div>
          <div className={tableHeadClass}>手机号</div>
          <div className={`${tableHeadClass} text-right`}>活跃天数</div>
          <div className={`${tableHeadClass} text-right`}>生图次数</div>
          <div className={`${tableHeadClass} text-right`}>生图积分</div>
          <div className={`${tableHeadClass} text-right`}>生视频次数</div>
          <div className={`${tableHeadClass} text-right`}>生视频积分</div>
          <div className={`${tableHeadClass} text-right`}>总积分</div>
          {users.map((item) => (
            <React.Fragment key={item.user_id}>
              <div className={`${tableCellClass} font-medium tabular-nums`}>{item.user_id}</div>
              <div className={`${tableCellClass} tabular-nums`}>
                {formatNullableText(item.phone)}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>{item.active_days}</div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {item.image_task_count}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {formatBillingPoints(item.image_cost, item.image_task_count)}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {item.video_task_count}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {formatBillingPoints(item.video_cost, item.video_task_count)}
              </div>
              <div className={`${tableCellClass} text-right font-medium tabular-nums`}>
                {formatPointCount(getMediaBillingPoints(item))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const ModelsReportTab: React.FC<{ models: BillingModelUsage[] }> = ({ models }) => {
  if (models.length === 0) {
    return <EmptyBlock text="暂无模型维度数据" />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_320px]">
      <div className="max-h-[460px] overflow-auto rounded-lg border border-dark-border">
        <div className="min-w-[640px]">
          <div className="grid grid-cols-[minmax(220px,1fr)_96px_104px_128px]">
            <div className={tableHeadClass}>模型</div>
            <div className={tableHeadClass}>类型</div>
            <div className={`${tableHeadClass} text-right`}>次数</div>
            <div className={`${tableHeadClass} text-right`}>积分</div>
            {models.map((item) => (
              <React.Fragment key={`${item.model}-${item.category}`}>
                <div
                  className={`${tableCellClass} min-w-0 break-words font-medium`}
                  title={item.model}
                >
                  {item.model}
                </div>
                <div className={tableCellClass}>{formatBillingCategory(item.category)}</div>
                <div className={`${tableCellClass} text-right tabular-nums`}>{item.task_count}</div>
                <div className={`${tableCellClass} text-right font-medium tabular-nums`}>
                  {formatBillingPoints(item.total_cost, item.task_count)}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <ModelPointsPieChart models={models} />
    </div>
  );
};

const DetailsReportTab: React.FC<{ details: GlobalBillingDetailUsage[] }> = ({ details }) => {
  if (details.length === 0) {
    return <EmptyBlock text="暂无积分明细" />;
  }

  return (
    <div className="max-h-[460px] overflow-auto rounded-lg border border-dark-border">
      <div className="min-w-[940px]">
        <div className="grid grid-cols-[104px_minmax(132px,1.1fr)_minmax(132px,1fr)_92px_112px_104px_124px_112px]">
          <div className={tableHeadClass}>日期</div>
          <div className={tableHeadClass}>用户 ID</div>
          <div className={tableHeadClass}>手机号</div>
          <div className={`${tableHeadClass} text-right`}>生图次数</div>
          <div className={`${tableHeadClass} text-right`}>生图积分</div>
          <div className={`${tableHeadClass} text-right`}>生视频次数</div>
          <div className={`${tableHeadClass} text-right`}>生视频积分</div>
          <div className={`${tableHeadClass} text-right`}>总积分</div>
          {details.map((item) => (
            <React.Fragment key={`${item.date}-${item.user_id}`}>
              <div className={`${tableCellClass} font-medium`}>{formatDateLabel(item.date)}</div>
              <div className={`${tableCellClass} tabular-nums`}>{item.user_id}</div>
              <div className={`${tableCellClass} tabular-nums`}>
                {formatNullableText(item.phone)}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {item.image_task_count}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {formatBillingPoints(item.image_cost, item.image_task_count)}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {item.video_task_count}
              </div>
              <div className={`${tableCellClass} text-right tabular-nums`}>
                {formatBillingPoints(item.video_cost, item.video_task_count)}
              </div>
              <div className={`${tableCellClass} text-right font-medium tabular-nums`}>
                {formatPointCount(getMediaBillingPoints(item))}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const GlobalBillingUsageModal: React.FC<GlobalBillingUsageModalProps> = ({
  open,
  usage,
  loading,
  error,
  dateRange,
  onClose,
  onRetry,
  onDateRangeChange,
}) => {
  const [activeTab, setActiveTab] = useState<ReportTab>('daily');
  const summaryItems = usage ? getGlobalBillingSummaryItems(usage) : [];

  return (
    <Modal
      title="全局积分报表"
      open={open}
      onClose={onClose}
      width="min(1360px, calc(100vw - 48px))"
    >
      {loading ? (
        <div className="space-y-4">
          <div className="h-16 animate-pulse rounded-lg border border-dark-border bg-dark-card" />
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="h-24 animate-pulse rounded-lg border border-dark-border bg-dark-card"
              />
            ))}
          </div>
          <div className="h-[460px] animate-pulse rounded-lg border border-dark-border bg-dark-card" />
        </div>
      ) : error ? (
        <div className="flex h-56 flex-col items-center justify-center gap-3 rounded-lg border border-dark-border bg-dark-card">
          <p className="text-sm font-medium text-dark-text">全局积分加载失败</p>
          <p className="max-w-sm text-center text-sm text-dark-text-secondary">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-theme-color px-4 text-sm text-theme-color transition-colors hover:bg-theme-color-10"
          >
            <RefreshCw className="h-4 w-4" />
            重试
          </button>
        </div>
      ) : usage ? (
        <div className="space-y-5">
          <BillingDateRangeFilter
            value={dateRange}
            onChange={onDateRangeChange}
            disabled={loading}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
            {summaryItems.map((item) => {
              const SummaryIcon = summaryIconByLabel[item.label] ?? Activity;
              return (
                <div
                  key={item.label}
                  className="rounded-lg border border-dark-border bg-dark-card px-4 py-3"
                >
                  <div className="flex items-center gap-2 text-xs text-dark-text-secondary">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-theme-color-10 text-theme-color">
                      <SummaryIcon className="h-3.5 w-3.5" />
                    </span>
                    {item.label}
                  </div>
                  <p className="mt-3 text-2xl font-semibold text-dark-text tabular-nums">
                    {item.value}
                  </p>
                  <p className="mt-2 truncate text-xs text-dark-text-secondary">{item.caption}</p>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 border-b border-dark-border pb-3">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-sm transition-colors ${
                    active
                      ? 'border-theme-color bg-theme-color-10 text-theme-color'
                      : 'border-dark-border text-dark-text-secondary hover:border-theme-color hover:text-dark-text'
                  }`}
                >
                  <TabIcon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {activeTab === 'summary' ? <SummaryTab usage={usage} /> : null}
          {activeTab === 'daily' ? <DailyReportTab usage={usage} /> : null}
          {activeTab === 'users' ? <UsersReportTab users={usage.users} /> : null}
          {activeTab === 'models' ? <ModelsReportTab models={usage.models} /> : null}
          {activeTab === 'details' ? <DetailsReportTab details={usage.details} /> : null}
        </div>
      ) : (
        <div className="flex h-48 items-center justify-center text-sm text-dark-text-secondary">
          暂无全局积分信息
        </div>
      )}
    </Modal>
  );
};

export default GlobalBillingUsageModal;
